import { Filter, Search, X } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { type EmailListParams, useEmails } from '@/api/hooks/use-emails'
import { DataTable } from '@/components/ui/data-table'
import { StatusBadge } from '@/components/ui/status-badge'
import { diffMs, extractDomain, formatMs, formatRelativeTime } from '@/lib/format'

const PER_PAGE = 25

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'queued', label: 'Queued' },
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'bounced', label: 'Bounced' },
  { value: 'complained', label: 'Complained' },
  { value: 'rejected', label: 'Rejected' },
]

const TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'transactional', label: 'Transactional' },
  { value: 'marketing', label: 'Marketing' },
]

type EmailRow = {
  id: string
  status: string
  to: string[]
  from: string
  marketing: boolean
  transactional: boolean
  ip: string | null
  created_at: string
  delivered_at: string | null
}

export function EmailListPage(): React.JSX.Element {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const apiKeyId = searchParams.get('api_key_id')
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')

  const params: EmailListParams = {
    page,
    per_page: PER_PAGE,
    ...(search && { search }),
    ...(statusFilter && { status: statusFilter }),
    ...(typeFilter && { type: typeFilter }),
    ...(apiKeyId && { api_key_id: apiKeyId }),
  }

  const { data, isLoading } = useEmails(params)

  const columns = [
    {
      key: 'status',
      label: 'Status',
      render: (row: EmailRow) => <StatusBadge status={row.status} />,
    },
    {
      key: 'to',
      label: 'To',
      render: (row: EmailRow) => <span className="font-mono text-sm">{row.to[0]}</span>,
    },
    {
      key: 'domain',
      label: 'Domain',
      render: (row: EmailRow) => extractDomain(row.from),
    },
    {
      key: 'type',
      label: 'Type',
      render: (row: EmailRow) => (
        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
          row.marketing
            ? 'bg-purple-100 text-purple-700'
            : 'bg-blue-100 text-blue-700'
        }`}>
          {row.marketing ? 'Marketing' : 'Transactional'}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Sent',
      sortable: true,
      render: (row: EmailRow) => formatRelativeTime(row.created_at),
    },
    {
      key: 'latency',
      label: 'Latency',
      render: (row: EmailRow) =>
        row.delivered_at ? (
          formatMs(diffMs(row.created_at, row.delivered_at))
        ) : (
          <span className="text-text-tertiary">--</span>
        ),
    },
  ]

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-text-primary">Emails</h1>

      {apiKeyId && (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-sm font-medium text-brand-700">
            Filtered by API key
            <button
              type="button"
              onClick={() => {
                setSearchParams({})
                setPage(1)
              }}
              className="ml-0.5 rounded-full p-0.5 hover:bg-brand-100"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        </div>
      )}

      {/* Search and filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            placeholder="Search by recipient, subject, or ID..."
            className="w-full rounded-lg border border-surface-3 bg-surface-0 py-2.5 pl-10 pr-4 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-text-tertiary" />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(1)
            }}
            className="rounded-lg border border-surface-3 bg-surface-0 px-3 py-2.5 text-sm"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value)
              setPage(1)
            }}
            className="rounded-lg border border-surface-3 bg-surface-0 px-3 py-2.5 text-sm"
          >
            {TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={data?.data ?? []}
        isLoading={isLoading}
        emptyMessage="No emails found"
        onRowClick={(row) => navigate(`/emails/${row.id}`)}
        pagination={
          data?.pagination
            ? {
                page: data.pagination.page,
                perPage: data.pagination.per_page,
                total: data.pagination.total,
              }
            : undefined
        }
        onPageChange={setPage}
      />
    </div>
  )
}
